import React from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import noImage from "../../images/no-image-placeholder.png";
import LakeViewIcon from "../../images/lake-view-icon.svg";
import AgentProfileCard from "../Common/AgentProfileCard";
import {
  thousandSeperatedValue,
  removeDecimalZeros,
} from "../../helpers/common";
import MatchReasonsPopover from "@/components/MatchReasonsPopover";

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d;
  }
};

const STATUS_LABEL = {
  Active: "Active",
  ActiveUnderContract: "Under Contract",
  Pending: "Pending",
};

const STATUS_VARIANT = {
  Active: "status-pill--active",
  ActiveUnderContract: "status-pill--contract",
  Pending: "status-pill--pending",
};

export const formatAddress = (l = {}) => {
  const cityState = [l.City, l.State].filter(Boolean).join(", ");
  const cityStateZip = [cityState, l.ZIP].filter(Boolean).join(" ");
  const street = l.Location || "";
  if (!street) return cityStateZip;
  if (!cityStateZip) return street;

  // Location usually already carries the city and state as a trailing
  // suffix, so appending cityStateZip would print it twice - but this must
  // be a genuine suffix match, not a substring anywhere in the street (a
  // "50 Greenwood Blvd" listing in Indianapolis is not already carrying
  // "Indianapolis").
  const normalizedStreet = street.toLowerCase().trim();
  const normalizedCityState = cityState.toLowerCase().trim();
  const normalizedZip = l.ZIP ? String(l.ZIP).toLowerCase().trim() : "";
  const streetHasCityState =
    normalizedCityState &&
    (normalizedStreet.endsWith(normalizedCityState) ||
      (normalizedZip &&
        normalizedStreet.endsWith(`${normalizedCityState} ${normalizedZip}`)));

  if (streetHasCityState) {
    // Street already carries city/state - append only the ZIP if it's
    // missing, rather than dropping it along with the rest of cityStateZip.
    const streetHasZip = normalizedZip && normalizedStreet.endsWith(normalizedZip);
    if (normalizedZip && !streetHasZip) {
      return `${street} ${l.ZIP}`;
    }
    return street;
  }

  return `${street} • ${cityStateZip}`;
};

const formatPrice = (p) => {
  if (p === null || p === undefined || p === "") return null;
  const n = parseFloat(p);
  if (Number.isNaN(n) || n <= 0) return null;
  return "$" + thousandSeperatedValue(removeDecimalZeros(n));
};

// Scores arrive as MySQL DECIMAL strings; null means "don't render a badge".
export const normalizeScore = (score) => {
  const n = Math.round(parseFloat(score));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const MatchScoreBadge = ({ score }) => {
  if (score === null) return null;
  return <span className="match-tag mls-match-tag">{score}% Match</span>;
};

const MlsMatchCard = ({ list, onClick, isSuggestion = false }) => {
  const navigate = useNavigate();
  const listingKey =
    _.get(list, "mlsListingKey") || _.get(list, "ListingKey");
  const photo = _.get(list, "photo");
  const missed = _.get(list, "missed") || [];
  const status = _.get(list, "StandardStatus", "");
  const beds = _.get(list, "Bedrooms");
  const baths = _.get(list, "Bathrooms");
  const sqft = _.get(list, "Sqft");
  const N2H = _.get(list, "N2H") || [];
  const price = formatPrice(_.get(list, "AskingPrice_Sell"));
  const score = normalizeScore(_.get(list, "score"));
  const fitPercent = normalizeScore(_.get(list, "fitPercent"));
  const reasons = _.get(list, "reasons");
  const listedDate = formatDate(_.get(list, "PublishedOn"));
  const agentFirstName = _.get(list, "userFirstName");
  const agentLastName = _.get(list, "userLastName");
  const agentCompany = _.get(list, "userCompany");
  const hasAgent = Boolean(agentFirstName || agentLastName);

  const handleClick = () => {
    sessionStorage.setItem("wantAdMatchesScrollY", String(window.scrollY));
    if (onClick) return onClick(listingKey, list);
    if (listingKey) navigate(`/mls-listing/${encodeURIComponent(listingKey)}`);
  };

  const statusLabel = STATUS_LABEL[status] || status;
  const statusVariant = STATUS_VARIANT[status] || "";

  return (
    <div
      className="mls-match-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="mls-match-card__media">
        <img
          src={photo || noImage}
          alt=""
          onError={(e) => {
            e.currentTarget.src = noImage;
          }}
        />
        <span className="mls-match-card__badge">MLS</span>
        {statusLabel && (
          <span className={`status-pill ${statusVariant}`.trim()}>
            {statusLabel}
          </span>
        )}
      </div>
      <div className="mls-match-card__body">
        {listedDate && (
          <div className="mls-match-card__listed-date">
            Listed {listedDate}
          </div>
        )}
        <div className="mls-match-card__price-row">
          {price && <div className="mls-match-card__price">{price}</div>}
          {isSuggestion ? (
            <MatchScoreBadge score={fitPercent} />
          ) : (
            score !== null && (
              <MatchReasonsPopover reasons={reasons} score={score}>
                <MatchScoreBadge score={score} />
              </MatchReasonsPopover>
            )
          )}
        </div>
        <div className="mls-match-card__address">{formatAddress(list)}</div>
        <div className="mls-match-card__facts">
          {beds != null && (
            <span className={missed.includes("beds") ? "is-missed" : ""}>
              {missed.includes("beds") && <em aria-hidden="true">&times;</em>}
              {beds} bd
            </span>
          )}
          {baths != null && (
            <span className={missed.includes("baths") ? "is-missed" : ""}>
              {missed.includes("baths") && <em aria-hidden="true">&times;</em>}
              {baths} ba
            </span>
          )}
          {sqft != null && (
            <span className={missed.includes("sqft") ? "is-missed" : ""}>
              {missed.includes("sqft") && <em aria-hidden="true">&times;</em>}
              {thousandSeperatedValue(sqft)} sqft
            </span>
          )}
        </div>
        {N2H?.length > 0 && (
          <div className="mls-match-card__chips">
            {N2H.slice(0, 8).map((n, i) => {
              const text = _.get(n, "Text", "");
              const icon = _.get(n, "Icon", "");
              return (
                <span key={i} className="mls-match-card__chip">
                  <img
                    src={
                      icon ? require("../../images/" + icon) : LakeViewIcon
                    }
                    alt=""
                  />
                  <span>{text}</span>
                </span>
              );
            })}
          </div>
        )}
        {hasAgent && (
          <div className="mls-match-card__agent property-agent-info">
            <AgentProfileCard
              isAgentCreated
              userFirstName={agentFirstName}
              userLastName={agentLastName}
              userCompany={agentCompany}
              showAvatar={false}
              showCompany={Boolean(agentCompany)}
              showProBadge={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MlsMatchCard;
